import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Activity, CheckCircle2, AlertTriangle, Loader2, Zap } from "lucide-react";

export const ExtractionHealthCard = () => {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/extraction-health");
      setHealth(data);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not load extraction health."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const runLiveTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/admin/extraction-health/test");
      setTestResult(data);
      if (data.success) {
        toast.success("AI key is active and funded — OCR extraction is operational.");
      } else {
        toast.error("AI key test failed — see the panel for the exact reason.", { duration: 8000 });
      }
    } catch (error) {
      setTestResult({ success: false, error: apiErrorMessage(error, "Live test request failed.") });
    } finally {
      setTesting(false);
    }
  };

  const keyOk = health?.keyConfigured;
  const issues = health?.recentWithIssues || [];

  return (
    <Card className="bg-card border-border" data-testid="extraction-health-card">
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <CardTitle className="font-display text-lg flex items-center gap-2">
              <Activity className="h-4.5 w-4.5 text-primary" /> Extraction health
            </CardTitle>
            <CardDescription>
              Live status of the OCR/AI pipeline. Run the live test after every deploy to confirm the
              AI key is present and funded.
            </CardDescription>
          </div>
          <Button
            size="sm"
            onClick={runLiveTest}
            disabled={testing}
            data-testid="extraction-live-test-button"
          >
            {testing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Zap className="h-4 w-4 mr-1.5" /> Run live AI test
              </>
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-6 w-64" />
            <Skeleton className="h-6 w-48" />
          </div>
        ) : health ? (
          <>
            <div className="flex flex-wrap items-center gap-2.5" data-testid="extraction-health-badges">
              {keyOk ? (
                <Badge
                  variant="outline"
                  className="border-[hsl(var(--status-verified)/0.4)] text-[hsl(var(--status-verified))]"
                  data-testid="extraction-key-status-badge"
                >
                  <CheckCircle2 className="h-3 w-3 mr-1" /> Emergent AI key configured
                </Badge>
              ) : (
                <Badge variant="destructive" data-testid="extraction-key-status-badge">
                  <AlertTriangle className="h-3 w-3 mr-1" /> Emergent AI key MISSING — OCR disabled
                </Badge>
              )}
              <Badge variant="outline" className="border-border text-muted-foreground">
                Engine: {health.ocrEngine === "gemini" ? "Gemini Vision OCR" : "Disabled"}
              </Badge>
              <Badge
                variant="outline"
                className={
                  health.issues24h > 0
                    ? "border-[hsl(var(--status-mismatch)/0.4)] text-[hsl(var(--status-mismatch))]"
                    : "border-border text-muted-foreground"
                }
                data-testid="extraction-issues-24h-badge"
              >
                {health.issues24h} extraction issue{health.issues24h === 1 ? "" : "s"} in last 24h
              </Badge>
              <Badge variant="outline" className="border-border text-muted-foreground">
                {health.recentSuccessfulAiExtractions} of last {health.recentTotal} orders AI-extracted OK
              </Badge>
            </div>

            {testResult && (
              <div
                className={`rounded-lg border p-3 text-sm ${
                  testResult.success
                    ? "border-[hsl(var(--status-verified)/0.4)] bg-[hsl(var(--status-verified)/0.08)] text-[hsl(var(--status-verified))]"
                    : "border-destructive/40 bg-destructive/10 text-destructive"
                }`}
                data-testid="extraction-test-result"
              >
                {testResult.success ? testResult.message : (
                  <>
                    <p className="font-medium">Live AI test failed:</p>
                    <p className="mt-1 font-mono text-xs break-all">{testResult.error}</p>
                    <p className="mt-2 text-xs">
                      If this mentions budget/quota, top up the Emergent Universal Key balance
                      (Profile → Universal Key → Add Balance), then redeploy is NOT required — retry the test.
                    </p>
                  </>
                )}
              </div>
            )}

            {issues.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="extraction-no-issues">
                No extraction errors recorded in the last {health.recentTotal} orders.
              </p>
            ) : (
              <ScrollArea className="max-h-56 w-full">
                <div className="space-y-2" data-testid="extraction-issues-list">
                  {issues.map((issue) => (
                    <div
                      key={issue.order_id}
                      className="rounded-lg border border-[hsl(var(--status-mismatch)/0.35)] bg-[hsl(var(--status-mismatch)/0.06)] p-3"
                      data-testid={`extraction-issue-${issue.order_id}`}
                    >
                      <div className="flex flex-wrap items-center gap-2 text-xs">
                        <span className="font-mono text-muted-foreground">{issue.order_id.slice(0, 8)}</span>
                        <span className="capitalize">{String(issue.request_type || "").replaceAll("_", " ")}</span>
                        <span className="text-muted-foreground">{issue.user_email}</span>
                        <span className="text-muted-foreground">
                          {issue.created_at ? new Date(issue.created_at).toLocaleString() : ""}
                        </span>
                      </div>
                      <p className="mt-1.5 font-mono text-xs text-[hsl(var(--status-mismatch))] break-all">
                        {issue.error}
                      </p>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </>
        ) : (
          <p className="text-sm text-muted-foreground">Extraction health is unavailable.</p>
        )}
      </CardContent>
    </Card>
  );
};
